/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * useEngagementHeatmap — single-source-of-truth data hook for the Engagement
 * Heatmap view.
 *
 * Follows the live-data view wiring pattern (memory-bank/systemPatterns.md):
 * three useState cells (data / loading / error), a useEffect with a
 * cancellation flag, and axios-client calls only. The hook returns an
 * imperative `refetch` handle so the view can drive a "Retry" button
 * without duplicating the fetch code.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  fetchEngagementHeatmap,
  type HeatmapResponse,
} from '@/api/narrative';

export interface UseEngagementHeatmapArgs {
  sinceDays: number;
  bucket: 'weekly' | 'daily';
  minN: number;
}

export interface UseEngagementHeatmapResult {
  data: HeatmapResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function mapError(e: any): string {
  const status = e?.response?.status as number | undefined;
  if (status === 403) {
    return 'Access Denied — Engagement Heatmap requires coordinator+ role.';
  }
  if (status === 401) {
    return 'Not authenticated — log in to see engagement data.';
  }
  if (status === 404) {
    return 'Engagement heatmap endpoint not found. Backend may not be deployed yet.';
  }
  if (status) {
    return `Backend error ${status}`;
  }
  return 'Could not reach the backend.';
}

export function useEngagementHeatmap(
  args: UseEngagementHeatmapArgs
): UseEngagementHeatmapResult {
  const { sinceDays, bucket, minN } = args;
  const [data, setData] = useState<HeatmapResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number>(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchEngagementHeatmap({ since_days: sinceDays, bucket, min_n: minN })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(mapError(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sinceDays, bucket, minN, nonce]);

  return { data, loading, error, refetch };
}
